import { IKeyedObject } from '@dirootie-extensions/typescript';
import { prop, ifElse, always, has } from 'ramda';
import { dispatch, isObject, isString, stubUndefined } from 'ramda-adjunct';
import * as Yup from 'yup';
import { errors } from './error-messages';
import { FieldValidationOptions } from './validations.types';

const applyMethod = <T extends IKeyedObject<any>>(
  obj: T,
  method: string,
  ...params: any[]
) => obj[method](...params);

const validators = {
  min: (obj: any, field: FieldValidationOptions) =>
    applyMethod(obj, 'min', field.min, errors.min(field.field!, field.min!)),
  max: (obj: any, field: FieldValidationOptions) =>
    applyMethod(obj, 'max', field.max, errors.max(field.field!, field.max!)),
  required: (obj: any, field: FieldValidationOptions) =>
    applyMethod(obj, 'required', errors.required(field.field!)),
  matches: (obj: any, field: FieldValidationOptions) =>
    applyMethod(obj, 'matches', field.regex, field.regexMessage),
  url: (obj: any, field: FieldValidationOptions) =>
    applyMethod(obj, 'url', field.urlMessage),
};

//Defaults
const defaults = {
  type: 'string',
  required: false,
  requiredMessage: errors.required,
  minMessage: errors.min,
  maxMessage: errors.max,
};

const courseNumberField = {
  regex: /^[A-Za-z]{3}\d{4}$/,
  regexMessage: () =>
    'Field must start with 3 characters and be followed by 4 numbers',
  required: true,
  field: 'number',
};

const nameField = {
  min: 1,
  max: 100,
  field: 'name',
  required: true,
};

const descriptionField = {
  min: 1,
  max: 1000,
  field: 'description',
  required: true,
};

const urlField = {
  min: 1,
  max: 2000,
  field: 'url',
};

export const commonValidators = {
  name: {
    ...nameField,
    fn: () => {
      let obj = Yup.string();
      obj = validators.min(obj, nameField);
      obj = validators.max(obj, nameField);
      obj = validators.required(obj, nameField);
      return obj;
    },
  },
  description: {
    ...descriptionField,
    fn: () => {
      let obj = Yup.string();
      obj = validators.min(obj, descriptionField);
      obj = validators.max(obj, descriptionField);
      obj = validators.required(obj, descriptionField);
      return obj;
    },
  },
  courseNumber: {
    ...courseNumberField,
    fn: () => {
      let obj = Yup.string();
      obj = validators.matches(obj, courseNumberField);
      obj = validators.required(obj, courseNumberField);
      return obj;
    },
  },
  url: {
    fn: () => {
      let obj = Yup.string();
      obj = validators.max(obj, urlField);
      obj = validators.url(obj, urlField);
      return obj;
    },
  },
};

export const sanitizeParam = dispatch([
  ifElse(
    isString,
    (s: any) => [
      prop(s, commonValidators).field,
      prop(s, commonValidators).fn(),
    ],
    stubUndefined
  ),
  ifElse(
    (x: any) => isObject(x) && has('fn', x),
    (field: FieldValidationOptions) => [field.field, field.fn],
    stubUndefined
  ),
  always,
  stubUndefined,
]);
